import { FormModal } from '@/components/form-modal.js';
import { Button } from '@/components/ui/button.js';
import { DialogDescription } from '@/components/ui/dialog.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { deleteClassTime, getClassTimesOfSchedule } from '@/lib/api-calls.js';
import { formatMeetingBlockDisplay } from '@/routes/_layout/scheduling/schedules/-components/formatting.js';
import { MinimalClassTime, MeetingBlock } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { ReactNode } from 'react';
import { z } from 'zod';

const formSchema = z.object({});

export interface DeleteClassTimeProps {
  trigger?: ReactNode;
  classTime: MinimalClassTime;
  afterDelete?: () => void;
  canArchive: boolean;
}

export function DeleteClassTime({ trigger, classTime, afterDelete, canArchive }: DeleteClassTimeProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteClassTimeMutate } = useMutation({
    mutationFn: deleteClassTime,
  });

  const onSubmit = ({ closeModal }: { closeModal: () => void }) => {
    if (!classTime?.id || !classTime?.scheduleId) {
      toast({ title: 'Missing class time or schedule ID' });
      return;
    }

    return deleteClassTimeMutate(classTime.id)
      .then(() => {
        toast({ title: 'Class time is deleted' });
        closeModal();
        afterDelete?.();
        queryClient.invalidateQueries(getClassTimesOfSchedule(classTime.scheduleId));
      })
      .catch((err) => {
        toast({
          title: 'Failed to delete class time',
          description: err.message,
        });
      });
  };

  return (
    <FormModal
      modalTitle="Delete class time"
      trigger={
        trigger || (
          <Button size="sm" variant="secondary" disabled={!canArchive}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )
      }
      onSubmit={onSubmit}
      dialogContentProps={{ className: 'max-w-md' }}
      submitButtonProps={{ variant: 'destructive', children: 'Delete' }}
      formSchema={formSchema}
      confirmBtnLabel="Delete"
    >
      {() => (
        <div>
          <DialogDescription>
            Are you sure you want to delete this class time? This action cannot be undone.
          </DialogDescription>
          <div className="mt-4 border border-slate-300 rounded-md p-4 text-black mb-4">
            <div>
              {classTime.meetingBlocks?.map((mb: MeetingBlock) => <p key={mb.id}>{formatMeetingBlockDisplay(mb)}</p>)}
            </div>
          </div>
        </div>
      )}
    </FormModal>
  );
}
